"use client"

import styles from "../../../../styles/finder/finder.module.scss"
import { IconButton } from "@mui/material"
import Image from "next/image"
import TextInputField from "../../TextInputField/TextInputField"
import SkinuvitaDropdown from "../../dropdown/dropdown"
import { ChangeEvent, ReactNode, RefObject, useEffect, useRef, useState } from "react"
import { indications } from "./insurances"
import { useUserStore } from "../store/finder_store"
import Checkbox from "../../checkbox/checkbox"

import StyledText from "../../../typography/typography-component"
import skColors from "../../../../styles/skColors"
import { useTranslations } from "next-intl"
import { ActiveHealthInsuranceItem } from "../../../pages/FinderClient"

const arrayContainsAnotherValue = (arr: string[]): string => {
  return arr.find((item) => {
    if (item !== indications.psoriasis && item !== indications.dermatitis && item !== indications.hand_eczema) {
      return true
    }
  })
}

function DialogComponent(props: {
  dialogRef: RefObject<HTMLDialogElement | null>
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const dialogElement = props.dialogRef.current

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dialogElement &&
        event.target instanceof Node &&
        !dialogElement.querySelector(`.${styles.dialogWindow}`)?.contains(event.target)
      ) {
        props.onClose()
        dialogElement.close()
      }
    }

    if (dialogElement) {
      dialogElement.addEventListener("click", handleClickOutside)
    }

    return () => {
      if (dialogElement) {
        dialogElement.removeEventListener("click", handleClickOutside)
      }
    }
  }, [props.onClose])

  return (
    <dialog className={styles.dialog} ref={props.dialogRef} onClose={props.onClose}>
      <div className={styles.dialogWindow}>
        <div className={styles.closeButtonArea}>
          <IconButton onClick={props.onClose}>
            <Image
              src={"/svg/close_circle.svg"}
              alt={"close"}
              width={24}
              height={24}
              style={{
                maxWidth: "100%",
                height: "auto",
              }}
            />{" "}
          </IconButton>
        </div>
        <div className={styles.dialogContent}>{props.children}</div>
      </div>
    </dialog>
  )
}

const Step1 = ({
  partnerInsurances,
  onDisabledChange,
}: {
  partnerInsurances: ActiveHealthInsuranceItem[]
  onDisabledChange: (value: boolean) => void
}) => {
  const t = useTranslations("index.finder")
  const dialogRef = useRef<HTMLDialogElement | null>(null)

  const { diagnoses, otherDiagnosis, toggleDiagnosis, setOtherDiagnosis, healthInsurance, setHealthInsurance } =
    useUserStore()
  const storeValue = useUserStore.getState()
  // const [selectOtherCheckbox, setSelectOtherCheckbox] = useState(storeValue.diagnoses.includes("other"))
  const [selectOtherDiagnose, setSelectOtherDiagnose] = useState<string>(
    arrayContainsAnotherValue(storeValue.diagnoses) ?? ""
  )
  const [isOpen, setIsOpen] = useState(false)

  // const [selectedHealthInsurance, setSelectedHealthInsurance] = useState<string>()

  const handleCheckboxChange = (diagnosis: indications) => {
    toggleDiagnosis(diagnosis)
  }

  const noDiagnosesSelected = diagnoses.length === 0
  const noHealthInsuranceSelected = !healthInsurance

  const isButtonEnabled = !noDiagnosesSelected && !noHealthInsuranceSelected

  useEffect(() => {
    onDisabledChange(!isButtonEnabled)
  }, [isButtonEnabled, onDisabledChange])

  return (
    <>
      <div className={styles.step1Content}>
        <StyledText style={{ paddingBottom: "16px" }} color={skColors.turquoise[600]} variant={"h3"}>
          {t("step1.title")}
        </StyledText>
        <StyledText style={{ paddingBottom: "32px" }} variant={"pRegular"}>
          {t("step1.subtitle")}
        </StyledText>
        <div className="centeredContent">
          <div className={styles.sectionHeading}>
            <StyledText color={skColors.darkgreen[600]} variant={"h4"}>
              {t("step1.diseaseHeading")}
            </StyledText>
            {/* Help icon and dialog removed from here */}
          </div>
          <div className={styles.indicationSelection}>
            <div className={styles.checkboxWithLabel}>
              <Checkbox
                id={indications.psoriasis}
                checked={diagnoses.includes(indications.psoriasis)}
                onChange={(event) => handleCheckboxChange(indications.psoriasis)}
              />
              <StyledText as={"label"} htmlFor={indications.psoriasis} variant={"pRegular"} size={12}>
                {t("step1.options.psoriasis")}
              </StyledText>
            </div>
            <div className={styles.checkboxWithLabel}>
              <Checkbox
                id={indications.dermatitis}
                checked={diagnoses.includes(indications.dermatitis)}
                onChange={(event) => handleCheckboxChange(indications.dermatitis)}
              />
              <StyledText as={"label"} htmlFor={indications.dermatitis} variant={"pRegular"} size={12}>
                {t("step1.options.dermatitis")}
              </StyledText>
            </div>
            <div className={styles.checkboxWithLabel}>
              <Checkbox
                id={indications.hand_eczema}
                checked={diagnoses.includes(indications.hand_eczema)}
                onChange={(event) => handleCheckboxChange(indications.hand_eczema)}
              />
              <StyledText as={"label"} htmlFor={indications.hand_eczema} variant={"pRegular"} size={12}>
                {t("step1.options.handEczema")}
              </StyledText>
            </div>
            {/*<div className={styles.checkboxWithLabel}>*/}
            {/*  <Checkbox*/}
            {/*    id={"other"}*/}
            {/*    checked={selectOtherCheckbox}*/}
            {/*    onChange={(event) => setSelectOtherCheckbox(!selectOtherCheckbox)}*/}
            {/*  />*/}
            {/*  <StyledText htmlFor={"other"} variant={"pRegular"} size={12}>*/}
            {/*    Ich habe eine andere Krankheit, nämlich:*/}
            {/*  </StyledText>*/}
            {/*</div>*/}
          </div>

          {/*<OtherConditionInput*/}
          {/*  onChange={(event) => handleOtherDiagnosisChange(event)}*/}
          {/*  value={otherDiagnosis ?? ""}*/}
          {/*  placeholder={"z.B. Vitiligo"}*/}
          {/*  style={{ marginTop: "8px" }}*/}
          {/*  isChecked={selectOtherCheckbox}*/}
          {/*/>*/}
          <StyledText
            style={{
              //marginTop: "16px",
              //marginBottom: "8px",
              paddingBottom: "16px",
            }}
            variant={"h4"}
            color={skColors.darkgreen[600]}
          >
            {t("step1.insuranceHeading")}
          </StyledText>

          <div style={{ paddingBottom: "48px" }}>
            <SkinuvitaDropdown
              currentlySelected={healthInsurance}
              healthInsurances={partnerInsurances.map((insurance) => insurance.name)}
              onSelect={(selectedValue) => setHealthInsurance(selectedValue)}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Step1
